import React, { useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ColumnDef,
  SortingState,
  PaginationState,
} from '@tanstack/react-table';
import { SearchIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { DataTable, DataTableHandle } from '@/components/ui/DataTable';
import {
  ColumnManagerPopover,
  ManagedColumn,
} from '@/components/ui/ColumnManagerPopover';
import { DateRangeFilter } from '@/components/ui/DateRangeFilter';
import { exportRows } from '@/components/ui/csvExport';
import { ExportCsvButton } from '@/components/ui/ExportCsvButton';
import { StatusFilterSelect } from '@/components/ui/StatusFilterSelect';
import { TableStateGate } from '@/components/ui/TableStateGate';
import { fetchSites, useSitesList } from '@/hooks/sites/useSitesList';
import { useColumnPreferences } from '@/hooks/useColumnPreferences';
import { useDateRangeColumnFilters } from '@/hooks/useDateRangeColumnFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useFilterUrlSync } from '@/hooks/useFilterUrlSync';
import { useUser } from '@/providers/UserProvider';
import { StudySiteData } from '@/types/study';
import { ViewDetailsButton } from '@/components/ui/ViewDetailsButton';
import { buildSiteColumns, siteColumnLabels } from './siteTableColumns';
import SiteDrawer from './components/SiteDrawer';

// Column-id -> backend `sort` param, for the columns whose server sort key
// isn't just the column id (the corpus-backed attribute sorts). Every other
// sortable column's backend key equals its column id (see
// Sites::SearchService::SORT_EXPRESSIONS).
const SORT_KEY_MAP: Record<string, string> = {
  pi_name: 'sort_pi',
  sponsor_name: 'sort_sponsor',
};

const DEFAULT_PAGE_SIZE = 25;

const SITES_TABLE_ID = 'sites';

const SITES_DEFAULT_COLUMN_ORDER = [
  'display_id',
  'site_name',
  'protocol_number',
  'sponsor_name',
  'country',
  'pi_name',
  'submitted_at',
  'expiration_date',
  'status',
  'view',
];

const SITES_MANAGEABLE_COLUMNS = [
  { id: 'display_id', defaultVisible: true, defaultPinned: 'left' as const },
  { id: 'site_name', defaultVisible: true, defaultPinned: 'left' as const },
  { id: 'protocol_number', defaultVisible: true },
  { id: 'sponsor_name', defaultVisible: true },
  { id: 'country', defaultVisible: true },
  { id: 'pi_name', defaultVisible: true },
  { id: 'submitted_at', defaultVisible: true },
  { id: 'expiration_date', defaultVisible: true },
  { id: 'status', defaultVisible: true },
];

// Columns with a DateRangeFilter — kept in one place so the URL sync (mount
// read + effect write) below doesn't drift from which columns actually have
// a filter UI wired up.
const DATE_RANGE_FILTER_COLUMNS = ['submitted_at', 'expiration_date'];

const Sites: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get('search') || ''
  );
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('status') || ''
  );
  const [countryFilter, setCountryFilter] = useState(
    searchParams.get('country') || ''
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const dataTableRef = useRef<DataTableHandle>(null);
  const [exportRowCount, setExportRowCount] = useState(0);
  const { userData } = useUser();
  const {
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    columnPinning,
    setColumnPinning,
    resetToDefaults,
  } = useColumnPreferences({
    tableId: SITES_TABLE_ID,
    userId: userData?.id,
    columns: SITES_MANAGEABLE_COLUMNS,
    defaultOrder: SITES_DEFAULT_COLUMN_ORDER,
  });
  const {
    columnFilters,
    setColumnFilters,
    dateRangeValue,
    setDateRangeValue,
    dateRangeUrlParams,
  } = useDateRangeColumnFilters({
    dateRangeColumns: DATE_RANGE_FILTER_COLUMNS,
    searchParams,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // A date-range filter or the country filter only ever run client-side (see
  // the plan doc — moving them server-side needs a jsonb-containment query and
  // a timezone-boundary port this change doesn't take on), so a table with
  // either active falls back to fetching the full search/status-filtered set
  // and paginating/filtering it in the browser, exactly as before this
  // change. Otherwise every page turn is a real request for just that page.
  const hasActiveDateRangeFilter = columnFilters.length > 0;
  const useServerPagination = !hasActiveDateRangeFilter && !countryFilter;

  const sortParam = sorting[0]
    ? (SORT_KEY_MAP[sorting[0].id] ?? sorting[0].id)
    : undefined;
  const orderParam = sorting[0]?.desc ? 'desc' : 'asc';

  // Any change that alters which rows/order come back must restart pagination
  // at page 1 — otherwise a user on page 3 could land past the end of a newly
  // narrowed result set.
  React.useEffect(() => {
    setPagination(prev =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [debouncedSearch, statusFilter, countryFilter, sorting, columnFilters]);

  // Mirror filter state into the URL so a filtered list survives a refresh and
  // can be shared or bookmarked — the behaviour Submissions already had. These
  // values are read back out of searchParams on mount (above).
  useFilterUrlSync(setSearchParams, {
    search: debouncedSearch,
    status: statusFilter,
    country: countryFilter,
    ...dateRangeUrlParams,
  });

  const {
    data: allData = [],
    isLoading,
    error,
    pagination: serverPaginationMeta,
    countries: countryOptions = [],
  } = useSitesList({
    search: debouncedSearch,
    status: statusFilter,
    ...(useServerPagination && {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      sort: sortParam,
      order: orderParam,
    }),
  });

  // Only relevant in the country-filter hybrid-fallback path: `allData` there
  // is the full search/status-filtered set (server pagination is off exactly
  // when a country filter is active), so this still filters correctly.
  const data = useMemo(
    () =>
      countryFilter
        ? allData.filter(s => s.countries.includes(countryFilter))
        : allData,
    [allData, countryFilter]
  );

  const handleOpenDrawer = useCallback((siteId: string) => {
    setSelectedSiteId(siteId);
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  // Shared site column definitions (also used by the Study page's Sites tab)
  // composed into this page's order, plus the index-only `view` column.
  const columns = useMemo(() => {
    const siteColumns = buildSiteColumns(t);
    return [
      siteColumns.display_id,
      siteColumns.site_name,
      siteColumns.protocol_number,
      siteColumns.sponsor_name,
      siteColumns.country,
      siteColumns.pi_name,
      siteColumns.submitted_at,
      siteColumns.expiration_date,
      siteColumns.status,
      {
        id: 'view',
        header: '',
        enableSorting: false,
        meta: { overflow: 'nowrap', exportable: false },
        cell: ({ row }: { row: { original: StudySiteData } }) => (
          <ViewDetailsButton
            onClick={() => handleOpenDrawer(row.original.id)}
          />
        ),
      },
    ] as ColumnDef<StudySiteData, unknown>[];
  }, [handleOpenDrawer, t]);

  const managedColumns: ManagedColumn[] = useMemo(() => {
    const labels = siteColumnLabels(t);
    return SITES_MANAGEABLE_COLUMNS.map(({ id }) => ({
      id,
      label: labels[id],
    }));
  }, [t]);

  // When server pagination is active, `data` only holds the current page —
  // DataTable's own exportCsv (which reads its in-memory row set) can no
  // longer produce "every filtered row". Fetch the full search/status/
  // sort-matching set (no page param) instead and build the CSV from that.
  const handleExportAll = useCallback(async () => {
    const { resources } = await fetchSites({
      search: debouncedSearch,
      status: statusFilter,
      sort: sortParam,
      order: orderParam,
    });
    exportRows({
      rows: resources,
      columns,
      columnOrder,
      columnVisibility,
      filename: 'sites.csv',
    });
  }, [
    debouncedSearch,
    statusFilter,
    sortParam,
    orderParam,
    columns,
    columnOrder,
    columnVisibility,
  ]);

  const handleRowClick = useCallback(
    (row: StudySiteData, e: React.MouseEvent | React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) {
        window.open(`/sites/${row.id}`, '_blank');
        return;
      }
      navigate(`/sites/${row.id}`);
    },
    [navigate]
  );

  return (
    <div className="h-full w-full flex flex-col">
      <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-white sticky top-0 z-10">
        <h1 className="text-gray-900 flex items-center gap-3">
          <span className="font-semibold text-2xl">{t('sites.title')}</span>
        </h1>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Filter bar — padded */}
        <div className="px-8 pt-8 pb-6 flex items-center gap-4">
          <div className="w-80 relative">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={t('sites.search_placeholder')}
              aria-label={t('sites.search_placeholder')}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#300091]/20 bg-white text-sm"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>
          <Select
            value={countryFilter || 'all'}
            onValueChange={v => setCountryFilter(v === 'all' ? '' : v)}
          >
            <SelectTrigger className="px-4 py-2 border border-[#e9eaeb] rounded-md text-sm text-gray-600 font-medium hover:bg-gray-50 bg-white h-auto w-auto gap-2 [&>svg]:opacity-100">
              <span>{countryFilter || t('sites.filter.country_label')}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {t('sites.filter.all_countries')}
              </SelectItem>
              {countryOptions.map(country => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <StatusFilterSelect
            value={statusFilter}
            onChange={setStatusFilter}
            allStatusesLabel={t('sites.filter.all_statuses')}
          />
          <DateRangeFilter
            label={t('sites.fields.approved')}
            value={dateRangeValue('submitted_at')}
            onChange={setDateRangeValue('submitted_at')}
          />
          <DateRangeFilter
            label={t('sites.fields.expiration')}
            value={dateRangeValue('expiration_date')}
            onChange={setDateRangeValue('expiration_date')}
          />
          <div className="ml-auto flex items-center gap-2">
            <ExportCsvButton
              dataTableRef={dataTableRef}
              filename="sites.csv"
              rowCount={exportRowCount}
              onExport={useServerPagination ? handleExportAll : undefined}
            />
            <ColumnManagerPopover
              columns={managedColumns}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              columnPinning={columnPinning}
              onColumnPinningChange={setColumnPinning}
              onReset={resetToDefaults}
            />
          </div>
        </div>

        {/* Table — inset with mx-8 so it can scroll horizontally */}
        <TableStateGate
          isLoading={isLoading}
          error={error}
          isEmpty={data.length === 0}
          errorMessage={t('sites.errors.failed_to_load')}
          emptyMessage={t('sites.empty_yet')}
        >
          <div className="mx-8 bg-white border border-[#e9eaeb] rounded-lg overflow-hidden">
            <DataTable
              ref={dataTableRef}
              columns={columns}
              data={data}
              onRowClick={handleRowClick}
              emptyMessage={t('sites.empty_yet')}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={setColumnVisibility}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              columnPinning={columnPinning}
              onColumnPinningChange={setColumnPinning}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
              sorting={sorting}
              onSortingChange={setSorting}
              pagination={{
                pageIndex: pagination.pageIndex,
                pageSize: pagination.pageSize,
                onPaginationChange: setPagination,
                ...(useServerPagination && {
                  server: {
                    pageCount: serverPaginationMeta?.total_pages ?? 0,
                    rowCount: serverPaginationMeta?.total ?? 0,
                  },
                }),
              }}
              onExportRowCountChange={setExportRowCount}
            />
          </div>
        </TableStateGate>
      </div>

      <SiteDrawer
        siteId={selectedSiteId}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
      />
    </div>
  );
};

export default Sites;
